<script lang="ts">
  import InlineSortingForm from "../sorting/InlineSortingForm.svelte";

  interface SelectedValue {
    displayName: string;
    featureRef: string;
  }

  interface TitleRow {
    filterId: string;
    title: string;
    filterTitleMode: string;
    hidden: boolean;
    selected: boolean;
    removeTrashcan: boolean;
    overflowBoxFilterTitle: boolean;
    hasExplanatoryText: boolean;
    showSelectedValues: boolean;
    selectedValues: SelectedValue[];
  }

  interface Props {
    productList: boolean;
    hasFacetValueTags: boolean;
    hideExpander: boolean;
    facetValueTagsJson?: string;
    sortingRuleString: string;
    selectedSortKey: string;
    sortingValuesJson: string;
    visibleTitles: TitleRow[];
    hiddenTitles: TitleRow[];
  }

  const {
    productList,
    hasFacetValueTags,
    hideExpander,
    facetValueTagsJson,
    sortingRuleString,
    selectedSortKey,
    sortingValuesJson,
    visibleTitles,
    hiddenTitles,
  }: Props = $props();

  function titleRowClass(row: TitleRow): string {
    return [
      "heureka_filterTitle",
      "heureka_filterTitle--ocComponents",
      row.removeTrashcan ? "heureka_filterTitle--removeTrash" : "",
      `heureka_filterTitle--${row.filterTitleMode}`,
      row.overflowBoxFilterTitle ? "ts_heureka_overflowBoxContainer" : "",
      row.hasExplanatoryText ? "heureka_filterTitle--explanatoryText" : "",
    ]
      .filter(Boolean)
      .join(" ");
  }
</script>

{#snippet titleRow(row: TitleRow)}
  <oc-row-v2 {...row.hidden ? { hidden: "" } : {}}>
    <div
      class={titleRowClass(row)}
      data-filter-id={row.filterId}
      data-filter-title={row.title}
      {...row.hidden ? { hidden: "" } : {}}
    >
      <dt class="heureka_filterTitle__title">{row.title}</dt>
      <div
        class={`heureka_filterTitle__selectedValues${row.selected ? " heureka_filterTitle__selectedValues--hasValues" : ""}`}
      >
        {#if row.showSelectedValues}
          <ul class="heureka_filterTitle__selectedValueList">
            {#each row.selectedValues as value (value.featureRef)}
              <li class="heureka_filterTitle__selectedValueItem" data-ts-feature-ref={value.featureRef}>
                <span>{value.displayName}</span><span class="heureka_filterTitle__selectedValues-seperator"
                  >,&nbsp;</span
                >
              </li>
            {/each}
          </ul>
        {/if}
        <div class="heureka_filterHeaderButtons"></div>
      </div>
      <dd class="heureka_filterTitle__values"></dd>
      <dd class="heureka_filterTitle__more">
        <oc-link-v2
          class="heureka_filterTitle__more-label--down"
          size="100"
          {...row.removeTrashcan ? { hidden: "" } : {}}>Mehr anzeigen</oc-link-v2
        >
        <oc-icon-v1 type="arrow-right" size="100" color="var(--oc-semantic-color-text-interactive)"></oc-icon-v1>
      </dd>
    </div>
  </oc-row-v2>
{/snippet}

<div class="heureka_filterTitles find_filterList_mobile find_filterList_mobile--ocComponents" id="heureka_filterTitles">
  <div class="heureka_denseMode heureka_denseMode--mobile">
    <dl
      id="heureka_filterTitlesDense"
      class="find_filterTitles find_filterTitles--bottom-bar"
      data-title="Filtern & Sortieren"
    >
      {#if hasFacetValueTags}
        <oc-row-v2
          hide-divider
          style="--custom-spacing-y: var(--oc-semantic-spacing-0); padding-bottom: var(--oc-semantic-spacing-75)"
        >
          <div class="heureka_filterTitle">
            <dt class="heureka_filterTitle__title oc-copy-100 oc-copy--bold">Gewählte Filter</dt>
            <dd class="heureka_filterTitle__values">
              <div class="ts_heureka_svelteFacetValueTags" data-serialized={facetValueTagsJson}></div>
            </dd>
          </div>
        </oc-row-v2>
      {/if}
      <oc-row-v2
        {...hasFacetValueTags
          ? {}
          : {
              "hide-divider": "",
              style: "--custom-spacing-y: var(--oc-semantic-spacing-0); padding-bottom: var(--oc-semantic-spacing-75)",
            }}
      >
        <div class="heureka_filterTitle" data-filter-id="find_filter_sorting">
          <dt class="heureka_filterTitle__title oc-copy-100 oc-copy--bold">Sortieren nach</dt>
          <dd class="heureka_filterTitle__values">
            <InlineSortingForm ruleString={sortingRuleString} {selectedSortKey} valuesJson={sortingValuesJson} />
          </dd>
        </div>
      </oc-row-v2>
      {#if productList}
        <oc-row-v2>
          <div
            class="heureka_filterTitle heureka_filterTitle--onlyTitle"
            data-filter-id="localNavigation"
            data-filter-title="Kategorien"
          >
            <dt class="heureka_filterTitle__title">Kategorien</dt>
            <dd class="heureka_filterTitle__selectedValues">
              <em class="js_selectedNavigationFilterSheet"></em>
            </dd>
            <dd class="heureka_filterTitle__more">
              <oc-icon-v1 type="arrow-right" size="100" color="var(--oc-semantic-color-text-interactive)"></oc-icon-v1>
            </dd>
          </div>
        </oc-row-v2>
      {/if}
      {#each visibleTitles as row (row.filterId)}
        {@render titleRow(row)}
      {/each}
    </dl>
  </div>
  <div
    class="heureka_expandedMode heureka_expandedMode--mobile heureka_expandedMode--ocComponents"
    {...hideExpander ? { hidden: "" } : {}}
  >
    <oc-expander-v1
      id="heureka_filterlistExpanderMobile"
      class="heureka_filterlistExpander heureka_filterlistExpander--mobile"
      variant="row"
      hide-fade=""
      cutoff-height="0"
      oc-aria-label="Weitere Filter"
    >
      <dl
        id="heureka_filterTitlesExpanded"
        class="find_filterTitles find_filterTitles--bottom-bar find_filterTitles--expanded"
        data-title="Filtern & Sortieren"
      >
        {#each hiddenTitles as row (row.filterId)}
          {@render titleRow(row)}
        {/each}
      </dl>
      <span class="heureka_expander-text" slot="show-more-text">Mehr Filter anzeigen</span>
      <span slot="show-less-text">Weniger Filter anzeigen</span>
    </oc-expander-v1>
  </div>
</div>
